import React, { useCallback, useEffect } from 'react';

import {
  LabelWith2ValuesValue,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { VerticalBarChart } from '../../../common/components/Charts/VerticalBarChart';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import ValidationAlert from '../../../app/components/ValidationAlert';

import {
  DateRangeFilterApplied,
  SuperAdminDateRangeFilter,
} from './SuperAdminDateRangeFilter';
import {
  DateRangeState,
  initDefaultLastDaysRange,
  toDateEnd,
  toDateStart,
} from './SuperAdminTopCompaniesBase';

export const SuperAdminInvoicesByPaymentTab: React.FC = () => {
  const spd = useSpdCore();
  const [dataByDays, setDataByDays] = React.useState<LabelWith2ValuesValue[]>(
    [],
  );
  const [dataByMonths, setDataByMonths] = React.useState<
    LabelWith2ValuesValue[]
  >([]);
  const [filters, setFilters] = React.useState<DateRangeState>({
    dateFrom: '',
    dateTo: '',
  });
  const [applied, setApplied] = React.useState<DateRangeFilterApplied>({
    dateFrom: '',
    dateTo: '',
  });
  const [errors, setErrors] = React.useState<string[]>([]);
  const [errorsMonthly, setErrorsMonthly] = React.useState<string[]>([]);
  const [filtersMonthly, setFiltersMonthly] = React.useState<DateRangeState>({
    dateFrom: '',
    dateTo: '',
  });

  const loadByDays = useCallback(
    (a: DateRangeFilterApplied) => {
      setErrors([]);
      spd.reports
        .reportInvoicesReceiptsByPayment(
          true,
          toDateStart(a.dateFrom),
          toDateEnd(a.dateTo),
          a.companyId,
        )
        .then(data => setDataByDays(data))
        .catch(async error => {
          setDataByDays([]);
          console.error(
            'Error during report invoices by payment by days',
            error,
          );
          setErrors(await responseErrors(error as ResponseError));
        });
    },
    [spd.reports],
  );

  const loadByMonths = useCallback(
    (a: DateRangeFilterApplied) => {
      setErrorsMonthly([]);
      spd.reports
        .reportInvoicesReceiptsByPayment(
          undefined,
          toDateStart(a.dateFrom),
          toDateEnd(a.dateTo),
          a.companyId,
        )
        .then(data => setDataByMonths(data))
        .catch(async error => {
          setDataByMonths([]);
          console.error(
            'Error during report invoices by payment by months',
            error,
          );
          setErrorsMonthly(await responseErrors(error as ResponseError));
        });
    },
    [spd.reports],
  );

  useEffect(() => {
    void initDefaultLastDaysRange(
      spd.params.getReportLastDays,
      range => {
        setFilters(range);
        setApplied(range);
        loadByDays(range);
      },
      async error => {
        console.error('Error during report last days param fetch', error);
        setErrors(await responseErrors(error as ResponseError));
      },
    );
  }, [spd.params, loadByDays]);

  useEffect(() => {
    loadByMonths({ dateFrom: '', dateTo: '' });
  }, [loadByMonths]);

  return (
    <>
      <SuperAdminDateRangeFilter
        filters={filters}
        setFilters={setFilters}
        withCompany={true}
        onApply={a => {
          setErrors([]);
          setDataByDays([]);
          setApplied(a);
          loadByDays(a);
        }}
      />
      <ValidationAlert
        show={errors.length > 0}
        errors={errors}
        className="mt-3"
      />
      {errors.length === 0 && (
        <VerticalBarChart
          title={`Faktury wg terminu płatności w zakresie: ${applied.dateFrom} - ${applied.dateTo}`}
          labels={dataByDays.map(o => o.label)}
          data={dataByDays.map(o => o.value1)}
          data2={dataByDays.map(o => o.value2)}
          datasetLabel="Zapłacone"
          backgroundColor="rgba(37, 99, 235, 0.8)"
          borderColor="rgba(37, 99, 235, 1)"
          datasetLabel2="Niezapłacone"
          backgroundColor2="rgba(147, 197, 253, 0.8)"
          borderColor2="rgba(147, 197, 253, 1)"
          stacked={true}
          key="invoicesByPaymentByDays"
        />
      )}
      <SuperAdminDateRangeFilter
        filters={filtersMonthly}
        setFilters={setFiltersMonthly}
        withCompany={true}
        monthPicker={true}
        applyLabel="Pokaż zestawienie miesięczne"
        onApply={a => {
          setErrorsMonthly([]);
          setDataByMonths([]);
          loadByMonths(a);
        }}
      />
      <ValidationAlert
        show={errorsMonthly.length > 0}
        errors={errorsMonthly}
        className="mt-3"
      />
      {errorsMonthly.length === 0 && (
        <VerticalBarChart
          title="Faktury wg terminu płatności w podziale na miesiące"
          labels={dataByMonths.map(o => o.label)}
          data={dataByMonths.map(o => o.value1)}
          data2={dataByMonths.map(o => o.value2)}
          datasetLabel="Zapłacone"
          backgroundColor="rgba(37, 99, 235, 0.8)"
          borderColor="rgba(37, 99, 235, 1)"
          datasetLabel2="Niezapłacone"
          backgroundColor2="rgba(147, 197, 253, 0.8)"
          borderColor2="rgba(147, 197, 253, 1)"
          stacked={true}
          key="invoicesByPaymentByMonths"
        />
      )}
    </>
  );
};
